import { Organization } from '../../models/Organization';
import { OrganizationDetail } from '../../models/Organizations';
import { State } from '../../reducers/State';
import { createSelector } from 'reselect';
import {
  pullRequestNameforProvider,
  displayNameForProvider,
  getPluralReviewWording,
} from 'utils/providerHelpers';
import URI from 'urijs';
import dayjs from 'dayjs';
import {
  accountOrganizationsSelector,
  ownsOrganization,
} from 'selectors/Account';
import {
  organizationNameFromRouteSelector,
  providerIdFromRouteSelector,
} from 'selectors/Router';

const getOrganization = (
  organizations: Organization[],
  organizationName: string | null,
  providerId: string | null
) => {
  return organizations?.find(
    (organization) =>
      organization.slug === organizationName &&
      organization.providerId === Number(providerId)
  );
};

export const organizationSelector = createSelector(
  accountOrganizationsSelector,
  organizationNameFromRouteSelector,
  providerIdFromRouteSelector,
  getOrganization
);

export const isH1SecurityOrganization = createSelector(
  (state: State) => state,
  organizationSelector,
  (state: State, organization?: Organization) => {
    return organization?.is_security_customer;
  }
);

export const isOrganizationOwner = createSelector(
  (state: State) => state,
  organizationSelector,
  (state: State, organization?: Organization) => {
    if (!organization) {
      return false;
    }
    return ownsOrganization(state, organization.uuid);
  }
);

export function organizationSlugSelector(state: State): string | undefined {
  return organizationSelector(state)?.slug;
}

export const organizationDetailSelector = (
  state: State
): OrganizationDetail | undefined => {
  const uuid = organizationSelector(state)?.uuid;

  if (uuid === undefined || state.organizations === undefined) {
    return undefined;
  }

  return state.organizations.byUUID[uuid];
};

export const organizationsSelector = (
  state: State
): { [uuid: string]: OrganizationDetail } | undefined => {
  if (state.organizations === undefined) {
    return undefined;
  }
  return state.organizations.byUUID;
};

export function organizationProviderIdSelector(
  state: State
): number | undefined {
  return organizationSelector(state)?.providerId;
}

export function organizationProviderNameSelector(
  state: State
): string | undefined {
  return organizationSelector(state)?.provider;
}

export const reviewWordingSelector = createSelector(
  organizationProviderNameSelector,
  pullRequestNameforProvider
);

export const pluralReviewWordingSelector = createSelector(
  organizationProviderNameSelector,
  getPluralReviewWording
);

export const providerDisplayNameSelector = createSelector(
  organizationProviderNameSelector,
  displayNameForProvider
);

export function getOrganizationHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/${providerId}/${slug}`;
}

export const organizationHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getOrganizationHref
);

export function getMetricsHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/metrics/${providerId}/${slug}/advanced`;
}

export const metricsHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getMetricsHref
);

export function getMetricsBaseHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/metrics/${providerId}/${slug}`;
}

export const metricsBaseHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getMetricsBaseHref
);

export const reviewLifecycleDurationHrefSelector = createSelector(
  metricsHrefSelector,
  (metricsHref?: string) =>
    buildMetricsHref({
      metricsHref,
      metric: 'average_review_lifecycle_hours',
    })
);

export const totalLineCountHrefSelector = createSelector(
  metricsHrefSelector,
  (metricsHref?: string) =>
    buildMetricsHref({
      metricsHref,
      metric: 'total_line_counts',
    })
);

export const percentTestsHrefSelector = createSelector(
  metricsHrefSelector,
  (metricsHref?: string) =>
    buildMetricsHref({
      metricsHref,
      metric: 'percent_test_lines_per_review',
    })
);

export const totalIssuesCaughtHrefSelector = createSelector(
  metricsHrefSelector,
  (metricsHref?: string) =>
    buildMetricsHref({
      metricsHref,
      metric: 'total_defects',
      segment: 'by_reviewer',
    })
);

export const totalCodeReviewsHrefSelector = createSelector(
  metricsHrefSelector,
  (metricsHref?: string) =>
    buildMetricsHref({
      metricsHref,
      metric: 'total_code_reviews_completed',
    })
);

export const commentsPerReviewHrefSelector = createSelector(
  metricsHrefSelector,
  (metricsHref?: string) =>
    buildMetricsHref({
      metricsHref,
      metric: 'average_comments_per_review',
    })
);

export const codeReviewSizeHrefSelector = createSelector(
  metricsHrefSelector,
  (metricsHref?: string) =>
    buildMetricsHref({
      metricsHref,
      metric: 'average_line_counts_per_review',
    })
);

interface MetricsHref {
  metricsHref?: string;
  metric: string;
  period?: string;
  segment?: 'by_author' | 'by_reviewer';
}

export const buildMetricsHref = ({
  metricsHref,
  metric,
  period,
  segment,
}: MetricsHref) => {
  if (!metricsHref) {
    return undefined;
  }

  return URI(metricsHref)
    .segment(segment ?? 'by_author')
    .query({
      metric,
      period: period ?? '30',
    })
    .toString();
};

export function getBenchmarksHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/metrics/${providerId}/${slug}/benchmarks`;
}

export const benchmarksHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getBenchmarksHref
);

export function getMembershipHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/settings/${providerId}/${slug}/membership`;
}

export const membershipHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getMembershipHref
);

export function getReviewSettingsHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return URI('/dash/settings/')
    .segment(providerId.toString())
    .segment(slug)
    .segment('review-settings')
    .toString();
}

export const reviewSettingsHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getReviewSettingsHref
);

export function getBillingInfoHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/settings/${providerId}/${slug}/billing/billing`;
}

export const billingInfoHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getBillingInfoHref
);

export function getBillingPlanHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/settings/${providerId}/${slug}/billing`;
}

export const billingPlanHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getBillingPlanHref
);

export function getProjectsHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/projects/${providerId}/${slug}`;
}

export const projectsHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getProjectsHref
);

export function getReviewsHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/pull-requests/${providerId}/${slug}`;
}

export const reviewsHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getReviewsHref
);

export function getNewReviewHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/new-review/${providerId}/${slug}`;
}

export const newReviewHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getNewReviewHref
);

export const proxySettingsHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getProxySettingsHref
);

export function getProxySettingsHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/settings/${providerId}/${slug}/proxy`;
}

export const ownerSettingsHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getOwnerSettingsHref
);

export function getOwnerSettingsHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/settings/${providerId}/${slug}/owner-settings`;
}

export function getOrganizationSettingsHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/settings/${providerId}/${slug}`;
}

export const organizationSettingsHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getOrganizationSettingsHref
);

export function getCommentHistoryHref(
  providerId?: number,
  slug?: string
): string | undefined {
  if (!providerId || !slug) {
    return undefined;
  }

  return `/dash/comments/${providerId}/${slug}`;
}

export const commentHistoryHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  getCommentHistoryHref
);

export const isNewCustomer = (
  organizationDetail?: OrganizationDetail
): boolean => {
  return dayjs().isBefore(dayjs(organizationDetail?.created_at).add(7, 'day'));
};

export const isSyncing = (organizationDetail?: OrganizationDetail): boolean => {
  return (
    !organizationDetail?.display_benchmarks && isNewCustomer(organizationDetail)
  );
};

export const isSyncingSelector = createSelector(
  organizationDetailSelector,
  isSyncing
);

export const isNewCustomerSelector = createSelector(
  organizationDetailSelector,
  isNewCustomer
);

export const isBenchmarksDisplayed = (
  organizationDetail?: OrganizationDetail
): boolean => {
  return organizationDetail?.display_benchmarks ?? false;
};

export const isBenchmarksDisplayedSelector = createSelector(
  organizationDetailSelector,
  isBenchmarksDisplayed
);

export const isUpdatingSelector = (state: State) =>
  state.organizations?.isUpdating;

export const errorSelector = (state: State) => state.organizations?.error;
